import React from "react";
import { TListingFull1Editable } from "../../../../domain/codecs/formEditable/ListingEditable";
import { ListingPropertySpecificsRequired, ListingPropertySpecificsOptional1, ListingPropertySpecificsOptional2 } from "../../../../domain/codecs/Listing";
import { CRMAutosaveIndicatorWrap } from "../CRM/CRMAutosaveIndicatorWrap/CRMAutosaveIndicatorWrap";
import { CRMCodecEditForm } from "../CRMCodecEditForm/CRMCodecEditForm";
import { CRMDropdownComponent } from "../CRMDropdownComponent/CRMDropdownComponent";
import { CRMSpacingColumn } from "../CRMSpacingColumn/CRMSpacingColumn";
import { CRMTitleSection } from "../CRMTitleSection/CRMTitleSection";
import { array } from "fp-ts/lib";
import { pipe } from "fp-ts/function";
import { TListingDataFormIO } from "../../../../domain/codecs/formIO/ListingDataFormIO";
import CRMInputLabelAndErrorWrapComponent from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { CRMLink } from "../CRMLink/CRMLink";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMTitleSubSection } from "../CRMTitleSubSection/CRMTitleSubSection";
import * as ListingCouncilTaxBand1 from "../../../../domain/models/ListingCouncilTaxBand1";
import * as ListingCondition1 from "../../../../domain/models/ListingCondition1";
import { CRMDropdownFormInput } from "../CRMFormInputs/CRMDropdownFormInput";
import { doesErrorKeyExist } from "../../../../shared/src/codecs/errors";

type TCRMListingSpecificsFormProps = {
    listingPageData: TListingDataFormIO;
    onChange: (listingForm: TListingFull1Editable) => void;
};

export const CRMListingSpecificsForm = (props: TCRMListingSpecificsFormProps): JSX.Element => {

    return (
        <CRMAutosaveIndicatorWrap status={props.listingPageData.output.form.status}>
            <CRMSpacingColumn spacing="large">
                <CRMTitleSection>
                    Specifics
                </CRMTitleSection>
                <CRMTitleSubSection>
                    Required
                </CRMTitleSubSection>
                <CRMCodecEditForm
                    codec={ListingPropertySpecificsRequired}
                    model={props.listingPageData.output.form.edited}
                    validationErrors={props.listingPageData.output.form.validationErrors}
                    onChange={(edited) => props.onChange({
                        ...props.listingPageData.output.form,
                        edited: {
                            ...props.listingPageData.output.form.edited,
                            ...edited,
                        }
                    })}
                />
                <CRMTitleSubSection>
                    Additional
                </CRMTitleSubSection>
                <CRMSpacingColumn spacing="medium">
                    <CRMCodecEditForm
                        codec={ListingPropertySpecificsOptional1}
                        model={props.listingPageData.output.form.edited}
                        validationErrors={props.listingPageData.output.form.validationErrors}
                        onChange={(edited) => props.onChange({
                            ...props.listingPageData.output.form,
                            edited: {
                                ...props.listingPageData.output.form.edited,
                                ...edited,
                            }
                        })}
                        overrideComps={{
                            // The superseded condition values still decode, so older
                            // listings render, but they are not offered: a valuer
                            // cannot file something new under a retired answer. The
                            // one exception is the value this listing already holds —
                            // without it the dropdown would show blank for a property
                            // whose condition is recorded, which reads as missing data
                            // rather than an old answer.
                            condition: (p) =>
                                <CRMDropdownFormInput
                                    label={p.label}
                                    model={p.model}
                                    onChange={p.onChange}
                                    disabled={p.disabled}
                                    displayError={doesErrorKeyExist("edited.condition", props.listingPageData.output.form.validationErrors)}
                                    options={pipe(
                                        ListingCondition1.optionsFor(props.listingPageData.output.form.edited.condition),
                                        array.map((value) => ({
                                            value,
                                            label: ListingCondition1.toDisplayName(value),
                                        }))
                                    )}
                                />,
                            council_tax_band: (p) =>
                                <CRMDropdownFormInput
                                    label={p.label}
                                    model={p.model}
                                    onChange={p.onChange}
                                    disabled={p.disabled}
                                    displayError={doesErrorKeyExist("edited.council_tax_band", props.listingPageData.output.form.validationErrors)}
                                    options={pipe(
                                        ListingCouncilTaxBand1.values,
                                        array.map((value) => ({
                                            value,
                                            label: ListingCouncilTaxBand1.toDisplayName(value),
                                        }))
                                    )}
                                />
                        }}
                    />

                    <CRMCodecEditForm
                        codec={ListingPropertySpecificsOptional2}
                        model={props.listingPageData.output.form.edited}
                        validationErrors={props.listingPageData.output.form.validationErrors}
                        onChange={(edited) => props.onChange({
                            ...props.listingPageData.output.form,
                            edited: {
                                ...props.listingPageData.output.form.edited,
                                ...edited,
                            }
                        })}
                    />
                </CRMSpacingColumn>
                
                {/* SELECT OCCUPIER FROM SELLER PARTY USERS */}
                {props.listingPageData.output.form.edited.property_occupied &&
                    <CRMSpacingColumn spacing="large">
                        <CRMLink
                            href={`/crm/listing/${props.listingPageData.input.listing_id}?visible_tab=seller_details&visible_sections=`}
                            target="_blank"
                            linkStyle="normal"
                        >
                            <WeightBold>Add seller party member</WeightBold>
                        </CRMLink>
                        <CRMInputLabelAndErrorWrapComponent label="Occupier">
                            <CRMDropdownComponent
                                options={pipe(
                                    props.listingPageData.output.read_only.all_seller_party_users_dropdown,
                                    array.map((user) => ({
                                        value: user.id,
                                        label: `${user.first_name} ${user.last_name}${user.is_legal_seller ? " (Legal seller)" : ""}`
                                    })),
                                    (userOptions) => [{value: "unknown", label: "Unknown"}, ...userOptions]
                                )}
                                value={props.listingPageData.output.form.edited.occupier_user_id ?? ""}
                                displayError={false}
                                onChange={(userId) => props.onChange({
                                    ...props.listingPageData.output.form,
                                    edited: {
                                        ...props.listingPageData.output.form.edited,
                                        occupier_user_id: userId === "unknown" ? null : userId
                                    }
                                })}
                            />
                        </CRMInputLabelAndErrorWrapComponent>
                    </CRMSpacingColumn>
                }
            </CRMSpacingColumn>
        </CRMAutosaveIndicatorWrap>
    );
};
